import React, { useState } from 'react';
import { initializeApp } from 'firebase/app';
import { getAuth, createUserWithEmailAndPassword } from 'firebase/auth';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db } from '../../config/firebaseConfig';
import styles from './CreateUserModal.module.css';
import { toast } from 'react-toastify';
import { useTheme } from '../../contexts/ThemeContext';
import { FaTimes, FaUserPlus, FaSpinner } from 'react-icons/fa';
import { deleteApp } from 'firebase/app';

function CreateUserModal({ employee, onClose }) {
  const { isDarkMode } = useTheme();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [role, setRole] = useState('employee'); // Default role
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const generateStrongPassword = (length = 12) => {
    const charset = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*()_+";
    let generatedPassword = "";
    for (let i = 0, n = charset.length; i < length; ++i) {
      generatedPassword += charset.charAt(Math.floor(Math.random() * n));
    }
    // Ensure password meets common complexity requirements (optional)
    if (!/[A-Z]/.test(generatedPassword)) generatedPassword += 'A';
    if (!/[a-z]/.test(generatedPassword)) generatedPassword += 'a';
    if (!/[0-9]/.test(generatedPassword)) generatedPassword += '1';
    if (!/[!@#$%^&*()_+]/.test(generatedPassword)) generatedPassword += '!';

    // Trim to desired length if extra characters were added
    return generatedPassword.slice(0, length);
  };

  const handleGeneratePassword = () => {
    const newPassword = generateStrongPassword();
    setPassword(newPassword);
    setConfirmPassword(newPassword);
    setError(''); // Clear error when generating
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!password || password !== confirmPassword) {
      setError('Şifreler eşleşmiyor veya boş.');
      return;
    }
    if (password.length < 6) {
        setError('Şifre en az 6 karakter olmalıdır.');
        return;
      }

    setIsLoading(true);

    // Define a unique name for the secondary app
    const secondaryAppName = `userCreationApp_${Date.now()}`;
    let secondaryApp = null; // Keep track of the secondary app instance

    try {
       // Get config from the default app instance
       const primaryAppConfig = auth.app.options;
       if (!primaryAppConfig) {
           throw new Error("Could not retrieve Firebase config from default app.");
       }

       // 1. Initialize secondary Firebase app using the retrieved config
       secondaryApp = initializeApp(primaryAppConfig, secondaryAppName);
       const secondaryAuth = getAuth(secondaryApp);

      // 2. Create Firebase Auth user using the secondary auth instance
      const userCredential = await createUserWithEmailAndPassword(secondaryAuth, employee.eMail, password);
      const user = userCredential.user;

      // 3. Create document in 'Users' collection (using the primary db instance)
      await setDoc(doc(db, 'Users', user.uid), {
        email: user.email,
        role: role,
        employeeId: employee.id,
        createdAt: serverTimestamp(),
        displayName: `${employee.eName} ${employee.eSurname}`
      });

      // 4. Update the employee's authorization field in Employees collection
      await setDoc(doc(db, 'Employees', employee.id), {
        authorization: [role] // Set the role as authorization
      }, { merge: true }); // Use merge to not overwrite other fields

      // 4. Send credentials email via Cloud Function (non-blocking for user creation)
      try {
        const response = await fetch(
          'https://us-central1-habcare-8ecad.cloudfunctions.net/sendAccountCredentialsEmailV2',
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              to: employee.eMail,
              password: password,
              displayName: `${employee.eName} ${employee.eSurname}`,
              loginUrl: 'https://management.habcarecro.com',
              fromName: 'HabcareCRO Yönetim',
            }),
          }
        );
        if (!response.ok) {
          throw new Error('Email function responded with non-OK status');
        }
        toast.success('Kullanıcı oluşturuldu ve giriş bilgileri e-posta ile gönderildi.');
      } catch (mailErr) {
        console.error('Error sending credentials email:', mailErr);
        toast.warn('Kullanıcı oluşturuldu fakat e-posta gönderilemedi. Lütfen bilgileri manuel iletiniz.');
      }

      // 5. Optionally, update the Employee document (using primary db)
      // const employeeRef = doc(db, 'Employees', employee.id);
      // await updateDoc(employeeRef, { userId: user.uid });

      toast.success(`Kullanıcı ${user.email} başarıyla oluşturuldu. Admin oturumu devam ediyor.`);
      onClose(); // Close modal on success

    } catch (error) {
      console.error('Error creating user:', error);
       if (error.code === 'auth/email-already-in-use') {
        setError('Bu e-posta adresi zaten başka bir kullanıcı tarafından kullanılıyor.');
        toast.error('E-posta zaten kullanımda.');
      } else if (error.code === 'auth/invalid-email') {
        setError('Geçersiz e-posta formatı.');
        toast.error('Geçersiz e-posta.');
      } else if (error.code === 'auth/weak-password') {
        setError('Şifre yeterince güçlü değil.');
        toast.error('Şifre zayıf.');
      } else {
        setError('Kullanıcı oluşturulurken bir hata oluştu: ' + error.message);
        toast.error('Kullanıcı oluşturma hatası.');
      }
    } finally {
      setIsLoading(false);
      // 6. Clean up: Delete the secondary app instance regardless of success/failure

      if (secondaryApp) {
        try {
           await deleteApp(secondaryApp);
           console.log(`Secondary app ${secondaryAppName} deleted.`);
        } catch (deleteError) {
            console.error(`Error deleting secondary app ${secondaryAppName}:`, deleteError);
        }
      }
    }
  };

  return (
    <div className={styles.modalBackdrop}>
      <div className={`${styles.modalContent} ${isDarkMode ? styles.darkMode : ''}`}>
        <button className={styles.closeButton} onClick={onClose}><FaTimes /></button>
        <h2 className={styles.modalTitle}>
          <FaUserPlus style={{ marginRight: '10px' }} />
          Çalışan İçin Kullanıcı Hesabı Oluştur
        </h2>

        <div className={styles.employeeInfo}>
          <p><strong>Çalışan:</strong> {employee.eName} {employee.eSurname}</p>
          <p><strong>E-posta:</strong> {employee.eMail}</p>
        </div>

        <form onSubmit={handleSubmit} className={styles.form}>
          <div className={styles.formGroup}>
            <label htmlFor="password">Şifre:</label>
            <div className={styles.passwordInputGroup}>
              <input
                type="text" // Use text to show generated password
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className={styles.input}
                autoComplete="new-password" // Help browser suggest not saving
              />
              <button
                type="button"
                onClick={handleGeneratePassword}
                className={styles.generateButton}
                title="Güçlü Şifre Oluştur"
              >
                Oluştur
              </button>
            </div>
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="confirmPassword">Şifreyi Onayla:</label>
            <input
              type="password"
              id="confirmPassword"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              className={styles.input}
              autoComplete="new-password"
            />
          </div>

          <div className={styles.formGroup}>
             <label htmlFor="role">Rol:</label>
             <select
               id="role"
               value={role}
               onChange={(e) => setRole(e.target.value)}
               className={styles.input}
             >
               <option value="employee">Çalışan</option>
               <option value="admin">Yönetici</option>
               <option value="Finans Yöneticisi">Finans Yöneticisi</option>
               <option value="Proje Yöneticisi">Proje Yöneticisi</option>
               <option value="Saha Koordinatörü">Saha Koordinatörü</option>
               <option value="İnsan Kaynakları">İnsan Kaynakları</option>
               <option value="SMO Lead">SMO Lead</option>
               {/* Add other roles as needed */}
             </select>
           </div>

          {error && <p className={styles.errorText}>{error}</p>}

          <div className={styles.buttonGroup}>
            <button type="button" className={styles.cancelButton} onClick={onClose} disabled={isLoading}>
              İptal
            </button>
            <button type="submit" className={styles.submitButton} disabled={isLoading}>
              {isLoading ? <FaSpinner className={styles.spinner} /> : <FaUserPlus />}
              {isLoading ? 'Oluşturuluyor...' : 'Kullanıcı Oluştur'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default CreateUserModal; 